import { useEffect, useRef, type ReactNode } from "react";

export type WizardHostMode = "dialog" | "view";

/**
 * wizard-dialog-mode: hosts the Inboeken/Uitboeken wizard either as a Jmix-style modal dialog over
 * the current screen (the default launch mode — parity with the Jmix DialogWindows wizards) or as
 * the pre-existing full-page view (still used for deep links and the dialog's "open as full page"
 * action).
 *
 * The rendered DOM shape is IDENTICAL in both modes — only classNames/attributes differ — and the
 * host sits at ONE stable render site in App.tsx, so flipping mode re-styles the mounted wizard
 * instead of remounting it. That matters: the wizards open a server-side WizardData draft on mount
 * and keep the operator's entries in local state only, so a remount would drop in-progress work and
 * orphan a draft.
 *
 * Intentionally page-local, not @fawkes/ui: the shared Modal is a 460px confirm-style dialog with
 * its own titled header, while the wizards bring their own header (title + Annuleren).
 */
export function WizardHost({
  mode,
  onDismiss,
  children,
}: {
  mode: WizardHostMode;
  /** Dialog-mode close request (Esc) — same as the wizard header's Annuleren. */
  onDismiss: () => void;
  children: ReactNode;
}) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const dialog = mode === "dialog";

  // Dialog mode: Esc = Annuleren. Skipped while a nested @fawkes/ui Modal is open — Modal has its
  // own document-level Esc handler (no stopPropagation), so without this guard one Esc would close
  // both the inner dialog and the wizard. A backdrop click deliberately does NOT close: a mid-entry
  // wizard is too easy to lose to a stray click (the Jmix wizard dialogs are equally non-dismissable).
  useEffect(() => {
    if (!dialog) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (document.querySelector(".fui-modal-overlay")) return;
      onDismiss();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [dialog, onDismiss]);

  // Dialog mode: focus the surface on open, restore the previously-focused element afterwards
  // (the kennelplanning Drawer pattern).
  useEffect(() => {
    if (!dialog) return;
    restoreRef.current = (document.activeElement as HTMLElement | null) ?? null;
    surfaceRef.current?.focus();
    return () => restoreRef.current?.focus();
  }, [dialog]);

  return (
    <div className={dialog ? "wizard-host wizard-host--dialog" : "wizard-host"}>
      <div
        ref={surfaceRef}
        className="wizard-host__surface"
        role={dialog ? "dialog" : undefined}
        aria-modal={dialog || undefined}
        tabIndex={dialog ? -1 : undefined}
      >
        {children}
      </div>
    </div>
  );
}
